/*eslint max-lines-per-function: ["error", 300]*/
/*eslint complexity: ["error", 47]*/
import React from 'react';
import { Pencil } from 'react-bootstrap-icons';
import { Form } from 'react-bootstrap';

import FormRow from '../../../app/components/FormRow';
import {
  CreateUpdateSalesInvoiceDto,
  CreateUpdateSalesInvoiceDtoPaymentFormEnum,
  GetCompanyDto,
  GetExaminedDto,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import {
  KSEFRoleEnum,
  PaymentFormEnum,
} from '../../../common/spdCore/validation/enums';
import { NewDatePicker } from '../../../app/components/NewDatePicker';
import { ExaminedSelector } from '../Examined/ExaminedSelector';
import CompanyByNameOrNipSelector from '../Company/CompanyByNameOrNipSelector';
import Button from '../../../common/components/Button';
import CurrencyControl from '../../../app/components/CurrencyControl';
import InvoiceIssuerSelector from '../InvoiceIssuer/InvoiceIssuerSelector';

import { KsefRoleSelector } from './KsefRoleSelector';
import { PaymentFormSelector } from './PaymentFormSelector';

interface SalesInvoiceBasicDataEditorProps {
  salesInvoice: CreateUpdateSalesInvoiceDto;
  propertyChange: (obj: Partial<CreateUpdateSalesInvoiceDto>) => void;
  readOnly?: boolean;
  issuerReadOnly?: boolean;
  company?: GetCompanyDto;
  examined?: GetExaminedDto;
  onEditExamined?: () => void;
  examinedRefreshKey?: number;
  onEditCompany?: () => void;
  companyRefreshKey?: number;
  recipient?: GetCompanyDto;
  onEditRecipient?: () => void;
  recipientRefreshKey?: number;
}

export const SalesInvoiceBasicDataEditor: React.FC<
  SalesInvoiceBasicDataEditorProps
> = props => {
  const { salesInvoice, propertyChange, readOnly } = props;

  const isCorrection = !!salesInvoice.originalInvoiceId;
  const companySelected = !!salesInvoice.companyId;
  const examinedSelected = !!salesInvoice.examinedId;

  const handleDateInvoiceChange = (invoiceDate: Date) => {
    propertyChange({
      invoiceDate,
      ...(salesInvoice.isPaid ? { dueDate: invoiceDate } : {}),
    });
  };

  const handleIsPaidChange = (isPaid: boolean) => {
    propertyChange({
      isPaid,
      dueDate: isPaid ? salesInvoice.invoiceDate : salesInvoice.dueDate,
      paidDate: isPaid ? (salesInvoice.paidDate ?? new Date()) : null,
    });
  };

  const handlePaidDateChange = (paidDate: Date) => {
    propertyChange({ paidDate });
  };

  const handleDateSaleChange = (saleDate: Date) => {
    propertyChange({ saleDate });
  };

  const handleDueDateChange = (dueDate: Date) => {
    propertyChange({ dueDate });
  };

  return (
    <>
      <FormRow controlId="number" label="Numer faktury">
        <Form.Control
          type="text"
          value={salesInvoice.number || ''}
          disabled={true}
          placeholder="Zostanie wygenerowany automatycznie"
        />
      </FormRow>
      <FormRow controlId="issuer" label="Wystawca">
        <InvoiceIssuerSelector
          value={salesInvoice.issuerId}
          onChange={option => propertyChange({ issuerId: option.value })}
          readOnly={props.issuerReadOnly}
        />
      </FormRow>
      <FormRow controlId="company" label="Firma">
        <div className="selector-with-button">
          <div className="selector-input">
            <CompanyByNameOrNipSelector
              value={salesInvoice.companyId || null}
              onChange={option =>
                propertyChange({ companyId: option?.value || null })
              }
              readOnly={readOnly || examinedSelected || isCorrection}
              clearable
              refreshKey={props.companyRefreshKey}
            />
          </div>
          {companySelected && props.company?.id && props.onEditCompany && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="selector-edit-btn"
              onClick={props.onEditCompany}
              title="Edytuj firmę"
            >
              <Pencil />
            </Button>
          )}
        </div>
      </FormRow>

      {props.company?.officialName && (
        <FormRow controlId="officialName" label="Nazwa oficjalna">
          <Form.Control
            type="text"
            value={props.company.officialName}
            disabled={true}
          />
        </FormRow>
      )}

      <FormRow controlId="examined" label="Badany">
        <div className="selector-with-button">
          <div className="selector-input">
            <ExaminedSelector
              value={salesInvoice.examinedId || null}
              onChange={option =>
                propertyChange({ examinedId: option?.value || null })
              }
              readOnly={readOnly || companySelected || isCorrection}
              clearable
              refreshKey={props.examinedRefreshKey}
              showNipOrPesel
            />
          </div>
          {examinedSelected && props.examined?.id && props.onEditExamined && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="selector-edit-btn"
              onClick={props.onEditExamined}
              title="Edytuj badanego"
            >
              <Pencil />
            </Button>
          )}
        </div>
      </FormRow>

      <FormRow controlId="recipient" label="Odbiorca">
        <div className="selector-with-button">
          <div className="selector-input">
            <CompanyByNameOrNipSelector
              value={salesInvoice.recipientId || null}
              onChange={option =>
                propertyChange({ recipientId: option?.value || null })
              }
              readOnly={readOnly}
              clearable
              refreshKey={props.recipientRefreshKey}
            />
          </div>
          {salesInvoice.recipientId &&
            props.recipient?.id &&
            props.onEditRecipient && (
              <Button
                variant="outline-secondary"
                size="sm"
                className="selector-edit-btn"
                onClick={props.onEditRecipient}
                title="Edytuj odbiorcę"
              >
                <Pencil />
              </Button>
            )}
        </div>
      </FormRow>

      {props.recipient?.officialName && (
        <FormRow
          controlId="recipientOfficialName"
          label="Nazwa oficjalna odbiorcy"
        >
          <Form.Control
            type="text"
            value={props.recipient.officialName}
            disabled={true}
          />
        </FormRow>
      )}

      {salesInvoice.recipientId && (
        <>
          <FormRow controlId="recipientRole" label="Rola odbiorcy (KSeF)">
            <KsefRoleSelector
              value={salesInvoice.recipientRole ?? null}
              onChange={v =>
                propertyChange({
                  recipientRole: (v as KSEFRoleEnum | null) ?? undefined,
                })
              }
              readOnly={readOnly}
            />
          </FormRow>
          {salesInvoice.recipientRole === KSEFRoleEnum.AdditionalBuyer && (
            <FormRow controlId="recipientShare" label="Udział (%) (KSeF)">
              <CurrencyControl
                value={salesInvoice.recipientShare}
                percentage={true}
                max={100}
                decimalScale={2}
                disabled={readOnly}
                onChange={value =>
                  propertyChange({
                    recipientShare: value ?? null,
                  })
                }
              />
            </FormRow>
          )}
          {salesInvoice.recipientRole === KSEFRoleEnum.OtherEntity && (
            <FormRow
              controlId="recipientRoleDescription"
              label="Opis roli (KSeF)"
            >
              <Form.Control
                as="textarea"
                rows={3}
                value={salesInvoice.recipientRoleDescription ?? ''}
                onChange={e =>
                  propertyChange({
                    recipientRoleDescription: e.target.value || null,
                  })
                }
                disabled={readOnly}
                maxLength={256}
                placeholder="Maksymalnie 256 znaków"
              />
            </FormRow>
          )}
          <FormRow controlId="recipientIdWew" label="IDWew odbiorcy (KSeF)">
            <Form.Control
              type="text"
              value={salesInvoice.recipientIdWew ?? ''}
              onChange={e =>
                propertyChange({
                  recipientIdWew: e.target.value || null,
                })
              }
              disabled={readOnly}
              maxLength={16}
              placeholder="Format: 1234567890-00001"
            />
          </FormRow>
        </>
      )}

      <FormRow controlId="paymentForm" label="Forma płatności">
        <PaymentFormSelector
          value={salesInvoice.paymentForm as unknown as PaymentFormEnum}
          onChange={v =>
            propertyChange({
              paymentForm:
                v as unknown as CreateUpdateSalesInvoiceDtoPaymentFormEnum,
            })
          }
          isDisabled={readOnly}
        />
      </FormRow>

      <FormRow controlId="invoiceDate" label="Data wystawienia">
        <NewDatePicker
          value={salesInvoice.invoiceDate || new Date()}
          dateChanged={handleDateInvoiceChange}
          readOnly={readOnly}
        />
      </FormRow>
      <FormRow controlId="saleDate" label="Data sprzedaży">
        <NewDatePicker
          value={salesInvoice?.saleDate || new Date()}
          dateChanged={handleDateSaleChange}
          readOnly={readOnly}
        />
      </FormRow>
      <FormRow controlId="dueDate" label="Termin płatności">
        <NewDatePicker
          value={salesInvoice?.dueDate}
          dateChanged={handleDueDateChange}
          readOnly={readOnly || !!salesInvoice.isPaid}
        />
      </FormRow>
      <FormRow controlId="isPaid" label="Zapłacono">
        <Form.Check
          type="checkbox"
          checked={!!salesInvoice.isPaid}
          onChange={e => handleIsPaidChange(e.target.checked)}
          disabled={readOnly}
        />
      </FormRow>
      {salesInvoice.isPaid && (
        <FormRow controlId="paidDate" label="Zapłacono dnia">
          <NewDatePicker
            value={salesInvoice.paidDate}
            dateChanged={handlePaidDateChange}
            readOnly={readOnly}
          />
        </FormRow>
      )}
      <FormRow controlId="sendEmail" label="Wyślij e-mailem">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Form.Check
            type="checkbox"
            checked={salesInvoice.sendEmail ?? true}
            onChange={e => propertyChange({ sendEmail: e.target.checked })}
            disabled={readOnly}
          />
          <Form.Text
            className={
              props.company?.email || props.examined?.email
                ? 'text-muted'
                : 'text-danger'
            }
          >
            {props.company?.emailInvoices ||
              props.examined?.email ||
              'Brak adresu e-mail firmy/badanego'}
          </Form.Text>
        </div>
      </FormRow>
    </>
  );
};
